import React, {
  createContext,
  ReactNode,
  useCallback,
  useState,
  useMemo,
  useContext,
} from "react";
import { IntlProvider } from "react-intl";
import en from "../locales/en.json";
import zh from "../locales/zh.json";

export enum SupportedLocales {
  English = "en",
  Chinese = "zh",
}

const messages: { [key in SupportedLocales]: any } = {
  [SupportedLocales.English]: en,
  [SupportedLocales.Chinese]: zh,
};

interface IntlContextType {
  locale: SupportedLocales;
  switchLanguage?: (locale: SupportedLocales) => void;
}

const IntlContext = createContext<IntlContextType | undefined>(undefined);

export const IntlProviderWrapper: React.FC<{
  children: ReactNode;
}> = ({ children }) => {
  const [locale, setLocale] = useState<SupportedLocales>(
    () =>
      (localStorage.getItem("appLocale") as SupportedLocales) ||
      SupportedLocales.English
  );

  const switchLanguage = useCallback((locale: SupportedLocales) => {
    localStorage.setItem("appLocale", locale);
    setLocale(locale);
  }, []);

  const providerValue = useMemo(
    () => ({ locale, switchLanguage }),
    [locale, switchLanguage]
  );

  return (
    <IntlProvider messages={messages[locale]} locale={locale} key={locale}>
      <IntlContext.Provider value={providerValue}>
        {children}
      </IntlContext.Provider>
    </IntlProvider>
  );
};

export default IntlContext;

export const useIntlContext = () => {
  const context = useContext(IntlContext);
  if (!context) {
    throw new Error(
      "useIntlContext must be used within an IntlProviderWrapper"
    );
  }
  return context;
};
